import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, Check } from 'lucide-react';
import { captureError } from '../utils/captureError';

/**
 * frontend/src/components/ErrorBoundary.jsx
 *
 * Until now a render error unmounted the whole tree and left a white page with
 * nothing on it — no message, no way back, and no record anywhere. Four such
 * crashes happened during one development session and each had to be
 * diagnosed from a stack trace pasted out of the browser console.
 *
 * This catches the error, shows something usable, and reports it.
 *
 * It must be a class: componentDidCatch has no hook equivalent.
 *
 * What it deliberately does NOT do is show the stack trace. Stack traces are
 * for Developer and Admin levels, in System Support. Everyone else gets a
 * message and a way to continue.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reference: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // A short reference the person can quote, so a report can be matched to
    // the stored occurrence without them reading a stack trace.
    const reference = Math.random().toString(36).slice(2, 8).toUpperCase();
    this.setState({ reference });

    captureError({
      kind: 'react_render',
      error,
      componentStack: info?.componentStack,
      reference,
      route: window.location?.pathname,
      boundary: this.props.name || 'app',
    });
  }

  reset = () => this.setState({ error: null, reference: null, copied: false });

  copy = () => {
    const { error, reference } = this.state;
    const text = [
      `Reference: ${reference}`,
      `Page: ${window.location?.pathname}`,
      `Error: ${error?.message || String(error)}`,
    ].join('\n');
    navigator.clipboard?.writeText(text)
      .then(() => {
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2000);
      })
      .catch(() => { /* clipboard refused; the reference is on screen anyway */ });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { reference, copied } = this.state;

    return (
      <div className="flex items-center justify-center p-6" style={{ minHeight: '60vh' }}>
        <div className="w-full max-w-lg rounded-xl p-6"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>

          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,200,73,.18)', color: '#8A6400' }}>
              <AlertTriangle size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
                This page stopped working
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Nothing you did caused this, and nothing you had saved has been lost.
                The problem has been reported automatically.
              </p>
            </div>
          </div>

          {reference && (
            <div className="rounded-lg px-3 py-2.5 mb-4 flex items-center justify-between gap-3"
              style={{ background: 'var(--surface-alt)' }}>
              <div className="min-w-0">
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Reference</p>
                <p className="font-bold tracking-wider" style={{ color: 'var(--text)', fontFamily: 'Consolas, monospace' }}>
                  {reference}
                </p>
              </div>
              <button onClick={this.copy}
                className="btn-secondary text-xs flex items-center gap-1.5 flex-shrink-0">
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Copied' : 'Copy details'}
              </button>
            </div>
          )}

          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            Quote that reference if you tell someone about it. Try again first —
            many of these do not happen twice.
          </p>

          <div className="flex gap-2">
            <button onClick={this.reset} className="btn-primary text-sm flex items-center gap-2">
              <RefreshCw size={14} /> Try again
            </button>
            <a href="/" className="btn-secondary text-sm flex items-center gap-2">
              <Home size={14} /> Go to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
